import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, CoinsIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export default function Hero() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = (action: 'vote' | 'create') => {
    if (user) {
      navigate('/dashboard', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const scrollToTopSnaps = () => {
    const topSnapsSection = document.getElementById('top-snaps-section');
    if (topSnapsSection) {
      topSnapsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="relative z-10 pb-8 sm:pb-10 md:pb-16 lg:w-full lg:pb-24 xl:pb-28">
          <div className="lg:max-w-[65%] xl:max-w-2xl">
            <main className="mt-6 sm:mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                className="text-center lg:text-left space-y-4 sm:space-y-6"
                initial="initial"
                animate="animate"
                variants={fadeIn}
              >
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary-100 to-primary-200 text-primary-800 shadow-md"
                >
                  <CalendarIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Join the Early Access</span>
                </motion.div>

                <motion.h1
                  className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-gray-900"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <span className="block mb-2">The World's Fastest Way</span>
                  <span className="pb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-500 leading-tight">
                    to Ask & Decide – Anonymously
                  </span>
                </motion.h1>

                <motion.p
                  className="mt-3 text-base sm:text-lg text-gray-500 sm:mt-5 md:mt-5 md:text-xl lg:mx-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  You're one SNAP away from instant answers, hot takes, and real talk - no name, no shame. Vote, share, or ask your burning questions without fear.<br/> Judgment-free, just like it should be.
                </motion.p>

                <motion.div
                  className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  {/* Create SNAP */}
                  <button
                    onClick={() => handleGetStarted('create')}
                    className="w-full sm:w-auto px-6 py-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-center transition-colors"
                  >
                    <div className="flex flex-col items-center sm:items-start">
                      <span className="text-lg font-semibold">Create SNAP</span>
                      <span className="text-sm font-light text-white/80">It’s free & anonymous</span>
                    </div>
                  </button>
                
                  {/* Show SNAPs */}
                  <button
                    onClick={scrollToTopSnaps}
                    className="w-full sm:w-auto px-6 py-4 rounded-xl bg-primary-100 hover:bg-primary-200 text-primary-700 text-center transition-colors"
                  >
                    <div className="flex flex-col items-center sm:items-start">
                      <span className="text-lg font-semibold">Show SNAPs</span>
                      <span className="text-sm font-light text-primary-700/70">See what’s trending</span>
                    </div>
                  </button>
                </motion.div>

                
                <motion.div
                  className="mt-4 sm:mt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0 }}
                >
                  <Link 
                    to="/conditions" 
                    className="inline-flex items-center text-yellow-600 hover:text-yellow-700 font-medium px-4 py-2 bg-yellow-100 rounded-full shadow-sm animate-pulse"
                  >
                    <CoinsIcon className="h-4 w-4 mr-1" />
                    <span className="text-base">Join now & get 25% more on CashSNAPs 💰</span>
                  </Link>
                </motion.div>
              </motion.div>
            </main>
          </div>
        </div>
        
      </div>
    </div>
  );
}